import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext.js'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'

// Material UI imports
import {
  CircularProgress,
  Collapse
} from '@mui/material'
import {
  Microsoft as MicrosoftIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material'

// Custom styled components
import {
  StyledContainer,
  StyledLoginCard,
  StyledMainTitle,
  StyledSubtitle,
  StyledLoginContainer,
  StyledLoginButton,
  StyledErrorAlert,
  StyledDescription
} from './Login.styles'

export default function Login() {
  const { loginRedirect, isAuthenticated, isLoading } = useAuth()
  const { instance, inProgress } = useMsal()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const hasNavigated = useRef(false)

  // Reset navigation flag when component mounts or when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      hasNavigated.current = false
    }
  }, [isAuthenticated])

  // Handle redirect response when returning from Azure AD
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise()
        if (response && !hasNavigated.current) {
          console.log('Redirect response received:', response)
          // Get intended route from sessionStorage, default to dashboard
          const intendedRoute = sessionStorage.getItem('intendedRoute') || '/dashboard'
          sessionStorage.removeItem('intendedRoute') // Clean up
          hasNavigated.current = true
          navigate(intendedRoute, { replace: true })
        }
      } catch (error) {
        console.error('Redirect handling error:', error)
        setError(error.message || 'Authentication failed')
      }
    }

    if (inProgress === InteractionStatus.None) {
      handleRedirect()
    }
  }, [instance, inProgress, navigate])

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None && !hasNavigated.current) {
      // Get intended route from sessionStorage, default to dashboard
      const intendedRoute = sessionStorage.getItem('intendedRoute') || '/dashboard'
      sessionStorage.removeItem('intendedRoute') // Clean up
      hasNavigated.current = true
      navigate(intendedRoute, { replace: true })
    }
  }, [isAuthenticated, inProgress, navigate])


  // Handle Azure AD login with redirect (alternative)
  const handleAzureLoginRedirect = async () => {
    setError('');
    setLoading(true);
    try {
      await loginRedirect();
      // Note: redirect will navigate away, so no need to handle success here
    } catch (err) {
      const msg = err?.message || 'Azure AD login redirect failed. Please try again.';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <StyledContainer>
      <StyledLoginCard>
        <StyledMainTitle variant="h1" component="h1">
          Ford Power Monitor
        </StyledMainTitle>

        <StyledSubtitle variant="h2" component="h2">
          Sign in with Azure AD
        </StyledSubtitle>

        <StyledLoginContainer>
          <Collapse in={!!error}>
            <StyledErrorAlert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ width: '100%', marginBottom: '16px' }}
            >
              {error}
            </StyledErrorAlert>
          </Collapse>

          <StyledLoginButton
            onClick={handleAzureLoginRedirect}
            disabled={loading || isLoading}
            startIcon={
              loading || isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <MicrosoftIcon />
              )
            }
            variant="contained"
            size="large"
          >
            {loading || isLoading ? 'Redirecting…' : 'Sign in with Microsoft'}
          </StyledLoginButton>

          <StyledDescription variant="body2">
            Use your organizational Microsoft account to access the Ford Power Monitor application.
          </StyledDescription>
        </StyledLoginContainer>
      </StyledLoginCard>
    </StyledContainer>
  )
}